import React, { useEffect, useState } from "react";
import { AlertOctagon, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";

const SOSButton = () => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(10);
  const [hotline, setHotline] = useState("+1-800-432-5847");

  useEffect(() => {
    if (!open) return;
    setCount(10);
    let n = 10;
    const iv = setInterval(() => {
      n -= 1;
      setCount(n);
      if (n <= 0) {
        clearInterval(iv);
        triggerCall();
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerCall = async () => {
    try {
      const { data } = await api.post("/patient/sos", { note: "Emergency SOS from patient app" });
      const line = data?.hotline || hotline;
      setHotline(line);
      window.location.href = `tel:${line.replace(/[^+\d]/g, "")}`;
    } catch {
      window.location.href = `tel:${hotline.replace(/[^+\d]/g, "")}`;
    } finally {
      setOpen(false);
    }
  };

  const cancel = () => setOpen(false);

  const radius = 70;
  const circ = 2 * Math.PI * radius;
  const offset = circ * ((10 - count) / 10);

  return (
    <>
      <button
        data-testid="sos-trigger-button"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-5 md:right-[calc(50%-215px+20px)] w-16 h-16 bg-[#EF4444] rounded-full flex flex-col items-center justify-center text-white shadow-lg shadow-red-500/40 z-40 active:scale-95 transition-all pulse-ring"
      >
        <AlertOctagon size={22} />
        <span className="text-[9px] font-bold tracking-wider mt-0.5">SOS</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center px-6"
            data-testid="sos-countdown-modal"
          >
            <motion.div
              initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, y: 20 }}
              className="bg-white rounded-[32px] p-6 w-full max-w-[380px] text-center"
            >
              <div className="text-red-500 font-instrument text-xl font-semibold mb-1">Emergency Alert</div>
              <div className="text-[#57534E] text-sm mb-6">Calling Health Circles panel in…</div>
              <div className="relative w-[180px] h-[180px] mx-auto mb-6">
                <svg width="180" height="180" className="rotate-[-90deg]">
                  <circle cx="90" cy="90" r={radius} stroke="#FEE2E2" strokeWidth="10" fill="none" />
                  <motion.circle
                    cx="90" cy="90" r={radius} stroke="#EF4444" strokeWidth="10" fill="none"
                    strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 1s linear" }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-6xl font-instrument font-bold text-[#EF4444]" data-testid="sos-countdown">{count}</div>
                  <div className="text-xs text-[#78716C] mt-1">seconds</div>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  data-testid="sos-call-now"
                  onClick={triggerCall}
                  className="btn-primary bg-[#EF4444] shadow-red-500/30"
                  style={{ background: "#EF4444" }}
                >
                  Call Now
                </button>
                <button data-testid="sos-cancel" onClick={cancel}
                  className="w-full py-3 rounded-2xl text-[#57534E] font-medium flex items-center justify-center gap-2">
                  <X size={18} /> Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SOSButton;
